/*
   Data layer for your own account: /api/v1/profile on the backend (routes_profile.py).

   Nothing there takes a user id, so nothing here can be pointed at somebody else. The name is the
   one field a person owns about themselves; role and active status are an administrator's and have
   no field to travel in.

   The sidebar reads the signed-in user from the stored session (lib/auth-token), not from a query,
   so every answer that carries the user is written back there too.
*/

import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import * as React from "react"

import { userFromWire, type User, type UserOutWire } from "@/features/auth"
import { readAuthSession, writeAuthSession } from "@/lib/auth-token"
import { ApiError } from "@/lib/errors"
import { http } from "@/lib/http"
import { qk } from "@/lib/query-keys"

interface ProfileUpdateWire {
  full_name: string | null
}

interface PasswordChangeWire {
  current_password: string
  new_password: string
}

/** The backend's ceiling for the name (schemas/auth.py). For passwords see password-rules. */
export const MAX_FIELD_LENGTH = 200

/** Write a fresher copy of the signed-in user into the stored session, when it differs. */
function storeUser(user: User): void {
  const session = readAuthSession()
  if (!session || session.user.id !== user.id) return
  const prev = session.user
  if (prev.name === user.name && prev.email === user.email && prev.role === user.role) return
  writeAuthSession({
    ...session,
    user: { id: user.id, email: user.email, name: user.name, role: user.role },
  })
}

export function profileQueryOptions() {
  return queryOptions({
    queryKey: qk.users.me(),
    queryFn: async ({ signal }) =>
      userFromWire(await http.get<UserOutWire>("/api/v1/profile", { signal })),
  })
}

/**
 * The signed-in user as the server has it now. Also refreshes the stored session, so a name an
 * administrator changed reaches the sidebar without signing in again.
 */
export function useProfileQuery() {
  const query = useQuery(profileQueryOptions())
  const { data } = query
  React.useEffect(() => {
    if (data) storeUser(data)
  }, [data])
  return query
}

/** One sentence for a failed profile call: the backend's own `detail` when it sent one. */
export function profileErrorMessage(cause: unknown): string {
  if (cause instanceof ApiError) {
    if (cause.status === 0) return "We couldn't reach the server. Check your connection."
    if (cause.isServer) return "Server error. Try again shortly."
    if (cause.isRateLimited) return "Too many attempts. Try again shortly."
    if (cause.message) return cause.message
  }
  return "Something went wrong. Please try again."
}

export function useUpdateProfileMutation() {
  const queryClient = useQueryClient()
  return useMutation<User, unknown, { name: string }>({
    mutationFn: async ({ name }) =>
      userFromWire(
        await http.patch<UserOutWire, ProfileUpdateWire>("/api/v1/profile", {
          full_name: name.trim() || null,
        }),
      ),
    // A read still in flight would land after the save with the old name, and write it back over
    // the cache and the sidebar.
    onMutate: () => queryClient.cancelQueries({ queryKey: qk.users.me() }),
    onSuccess: (user) => {
      queryClient.setQueryData(qk.users.me(), user)
      storeUser(user)
    },
  })
}

export function useChangePasswordMutation() {
  const queryClient = useQueryClient()
  return useMutation<User, unknown, { currentPassword: string; newPassword: string }>({
    // A wrong current password is a 400 (routes_profile.py), deliberately not a 401: the session is
    // valid, and a 401 would have lib/http treat it as expired and sign the person out.
    mutationFn: async ({ currentPassword, newPassword }) =>
      userFromWire(
        await http.post<UserOutWire, PasswordChangeWire>("/api/v1/profile/password", {
          current_password: currentPassword,
          new_password: newPassword,
        }),
      ),
    onSuccess: (user) => {
      queryClient.setQueryData(qk.users.me(), user)
      // The server revokes every refresh token of the account on a password change, this
      // session's included. Dropping it here means the session ends cleanly when its access
      // token does, instead of failing a refresh that can no longer succeed.
      const session = readAuthSession()
      if (session?.refreshToken) {
        writeAuthSession({ ...session, refreshToken: null, refreshExpiresAt: null })
      }
    },
  })
}

/** When this session's access token runs out, or null when it is not known. */
export function sessionEndsAt(): Date | null {
  const expiresAt = readAuthSession()?.expiresAt ?? 0
  return expiresAt > 0 ? new Date(expiresAt) : null
}
